import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";

export default function AchievementCard({
  achievement = {},
  parentType = "achievements",
  currentUser = null,
  onReact = () => {},
  reactionCountsFromParent = null,
  reactions = null,
}) {
  const parentId = achievement?.id || achievement?.docId || null;
  const [reactionCounts, setReactionCounts] = useState(reactionCountsFromParent ?? achievement?.reactionCounts ?? {});
  const [myReaction, setMyReaction] = useState(null);

  useEffect(() => {
    if (reactionCountsFromParent) setReactionCounts(reactionCountsFromParent);
    else setReactionCounts(achievement?.reactionCounts || {});
  }, [reactionCountsFromParent, achievement?.reactionCounts]);

  useEffect(() => {
    if (!parentId) return undefined;
    if (reactionCountsFromParent) return undefined;
    const ref = doc(db, parentType, parentId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      setReactionCounts(snap.data().reactionCounts || {});
    }, (err) => { console.error("AchievementCard reactionCounts listener error:", err); });
    return () => unsub();
  }, [parentId, parentType, reactionCountsFromParent]);

  useEffect(() => {
    if (!parentId || !currentUser?.uid) { setMyReaction(null); return undefined; }
    const myRef = doc(db, parentType, parentId, "reactions", currentUser.uid);
    const unsub = onSnapshot(myRef, (snap) => {
      if (!snap.exists()) { setMyReaction(null); return; }
      const t = snap.data()?.type;
      const isId = reactions?.some((r) => r.id === t);
      if (isId) setMyReaction(t);
      else {
        const found = reactions?.find((r) => r.emoji === t);
        setMyReaction(found ? found.id : null);
      }
    }, (err) => { console.error("AchievementCard myReaction listener error:", err); });
    return () => unsub();
  }, [parentId, parentType, currentUser?.uid, reactions]);

  const buttons = reactions && reactions.length ? reactions.slice(0, 6) : [
    { id: "like", emoji: "👍", label: "Like" },
    { id: "fire", emoji: "🔥", label: "Fire" },
    { id: "celebrate", emoji: "🎉", label: "Celebrate" },
    { id: "funny", emoji: "😂", label: "Funny" },
  ];

  function handleReactClick(reactionId) {
    onReact(reactionId, myReaction);
  }

  function getCountForId(id) {
    return reactionCounts?.[id] ?? 0;
  }

  return (
    <div className="achievement-card">
      <div className="ach-left">
        <img src={achievement.icon || achievement.iconUrl || "/images/ach-placeholder.png"} alt={achievement.title || "Achievement"} width={48} height={48} />
      </div>

      <div className="ach-body">
        <div className="ach-title">{achievement.title || achievement.name || "Untitled"}</div>
        <div className="ach-desc">{achievement.description || ""}</div>
      </div>

      <div className="ach-actions">
        {buttons.map((r) => (
          <button
            key={r.id}
            className={`react-btn ${myReaction === r.id ? "active" : ""}`}
            onClick={() => handleReactClick(r.id)}
            aria-pressed={myReaction === r.id}
            title={r.label}
          >
            <span className="emoji">{r.emoji}</span>
            <span className="count">{getCountForId(r.id)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

AchievementCard.propTypes = {
  achievement: PropTypes.object,
  parentType: PropTypes.string,
  currentUser: PropTypes.object,
  onReact: PropTypes.func,
  reactionCountsFromParent: PropTypes.object,
  reactions: PropTypes.array,
};
